import { useApiClient } from "@/api/client";
import dayjs, { Dayjs } from "dayjs";
import DocumentDimensionDTO = App.Data.DocumentDimensionDTO;
import DimensionDO = App.Data.DimensionDO;
import PeriodSelectionDTO = App.Data.Explorer.PeriodSelectionDTO;
import { usePropertyStore } from "@/modules/explorer/PropertyStore";
import { useExplorerStore } from "@/modules/explorer/store";

const PERIOD_BOUND_FORMAT = "YYYY-MM-DD HH:mm:ss";

const DAY_OF_WEEK_IDS: Record<string, number> = {
	Monday: 1,
	Tuesday: 2,
	Wednesday: 3,
	Thursday: 4,
	Friday: 5,
	Saturday: 6,
	Sunday: 7,
};

const padHour = (hour: number) => String(hour).padStart(2, "0");

let filterableDatetimeDimensions: DimensionDO[] | null = null;

const findDayDimensionInFamily = async (
	familyNameClean: string | null
): Promise<DimensionDO | null> => {
	if (!familyNameClean) return null;

	if (!filterableDatetimeDimensions) {
		const response = await useApiClient("explorer").getDimensions({
			"filter[type]": "datetime",
			"filter[is_filterable]": true,
		});
		if (!response.isOk()) return null;
		filterableDatetimeDimensions = response.value;
	}

	return (
		filterableDatetimeDimensions.find(
			(candidate) =>
				candidate.family_name_clean === familyNameClean &&
				candidate.granularity === "day"
		) ?? null
	);
};

const DRILLDOWN_GRANULARITIES = [
	"60min",
	"day",
	"week",
	"month",
	"quarter",
	"year",
];

export const findFinerGranularityDimension = (
	dimension: DocumentDimensionDTO,
	axis: "horizontal" | "legend"
): DimensionDO | null => {
	const rung = DRILLDOWN_GRANULARITIES.indexOf(dimension.granularity ?? "");
	if (rung <= 0) return null;

	const finer = DRILLDOWN_GRANULARITIES[rung - 1];
	const propertyStore = usePropertyStore();

	const candidate = propertyStore.dimensions.find(
		(option: DimensionDO) =>
			option.family_name_clean === dimension.family_name_clean &&
			option.granularity === finer
	);
	if (!candidate) return null;

	// The same two tests the axis dialog offers a dimension under (Selection.vue).
	// Skipping them can put a dimension the vertical's data source does not carry
	// onto the axis, which comes back as an empty graph.
	if (
		!propertyStore.isAvailable(candidate, null, axis) ||
		(axis === "legend" &&
			!propertyStore.isAvailableAsLegend(
				candidate,
				useExplorerStore().document
			))
	) {
		return null;
	}

	return candidate;
};

export const granuleToDateRange = (
	granularity: string | null,
	label: string
): [Dayjs, Dayjs] | null => {
	switch (granularity) {
		case "day": {
			const day = dayjs(label);
			return day.isValid() ? [day.startOf("day"), day.endOf("day")] : null;
		}
		case "week": {
			// %G-%V is the ISO week-year, which differs from the calendar year
			// around New Year. ISO week 1 is by definition the week containing
			// 4 January, so counting weeks from there avoids needing an
			// isoWeekYear setter (dayjs only types the getter).
			const [year, week] = label.split("-").map(Number);
			if (!Number.isInteger(year) || !Number.isInteger(week)) return null;
			const start = dayjs(`${year}-01-04`)
				.startOf("isoWeek")
				.add(week - 1, "week");
			return start.isValid() ? [start, start.endOf("isoWeek")] : null;
		}
		case "month": {
			const month = dayjs(`${label}-01`);
			return month.isValid()
				? [month.startOf("month"), month.endOf("month")]
				: null;
		}
		case "quarter": {
			const [year, quarter] = label.split("-Q").map(Number);
			if (!Number.isInteger(year) || !Number.isInteger(quarter)) return null;
			const start = dayjs().year(year).quarter(quarter).startOf("quarter");
			return start.isValid() ? [start, start.endOf("quarter")] : null;
		}
		case "year": {
			const year = dayjs(`${label}-01-01`);
			return year.isValid() ? [year.startOf("year"), year.endOf("year")] : null;
		}
		default:
			return null;
	}
};

const rangeToPeriodSelection = ([from, to]: [
	Dayjs,
	Dayjs
]): PeriodSelectionDTO => ({
	from: from.format(PERIOD_BOUND_FORMAT),
	to: to.format(PERIOD_BOUND_FORMAT),
	from_parsed: from.toDate(),
	to_parsed: to.toDate(),
});

const PERIOD_SELECTION_FAMILY = "periodsubjectdate";

export const buildPeriodSelectionForLabel = (
	dimension: DocumentDimensionDTO,
	label: string,
	currentSelection?: PeriodSelectionDTO | null
): PeriodSelectionDTO | null => {
	if (dimension.family_name_clean !== PERIOD_SELECTION_FAMILY) return null;

	const range = granuleToDateRange(dimension.granularity, label.trim());
	if (!range) return null;

	return rangeToPeriodSelection(clampToSelection(range, currentSelection));
};

const clampToSelection = (
	[from, to]: [Dayjs, Dayjs],
	selection?: PeriodSelectionDTO | null
): [Dayjs, Dayjs] => {
	const bound = (value: Date | null | undefined) => {
		const parsed = value ? dayjs(value) : null;
		return parsed?.isValid() ? parsed : null;
	};

	const start = bound(selection?.from_parsed);
	const end = bound(selection?.to_parsed);
	const clamped: [Dayjs, Dayjs] = [
		start?.isAfter(from) ? start : from,
		end?.isBefore(to) ? end : to,
	];

	return clamped[1].isBefore(clamped[0]) ? [from, to] : clamped;
};

/**
 * Builds a filter for one label on a datetime dimension, or null when the
 * granularity has no filter representation.
 *
 * The backend only emits filter SQL for minute, hour, dayofweek and day
 * granularities (DimensionDateColumn::filter()). dayofweek and 60min filter on
 * a discrete id; the calendar granularities become a date range on the family's
 * `day` sibling, since the coarser dimensions are not filterable themselves.
 */
export const buildDatetimeFilterConfiguration = async (
	dimension: DocumentDimensionDTO,
	label: string
) => {
	const granularity = dimension.granularity;
	const trimmed = label.trim();

	if (granularity === "dayofweek") {
		const id = DAY_OF_WEEK_IDS[trimmed];
		return id ? buildDiscreteDatetimeFilter(dimension, id, trimmed) : null;
	}

	if (granularity === "60min") {
		// The 60min axis is hour-of-day (formatDateTime(..., '%R') drops the
		// date), so the hour is exactly what hour(column) IN (...) filters on.
		const hour = Number(trimmed.split(":")[0]);
		if (!Number.isInteger(hour) || hour < 0 || hour > 23) return null;
		return buildDiscreteDatetimeFilter(
			dimension,
			hour,
			`${padHour(hour)}:00 - ${padHour(hour + 1)}:00`
		);
	}

	const range = granuleToDateRange(granularity, trimmed);
	if (!range) return null;

	const target =
		granularity === "day"
			? dimension
			: ((await findDayDimensionInFamily(
					dimension.family_name_clean
			  )) as unknown as DocumentDimensionDTO | null);
	if (!target) return null;

	return {
		dimension: target,
		isApplied: true,
		isQuickFilter: false,
		isDefault: false,
		valuesAreIncluded: true,
		values: [
			{
				id: 1,
				level: null,
				selection_depth: null,
				model: null,
				sort_order: null,
				label: "n/a",
				filter_period: rangeToPeriodSelection(range),
			},
		],
	};
};

const buildDiscreteDatetimeFilter = (
	dimension: DocumentDimensionDTO,
	id: number,
	label: string
) => ({
	dimension,
	isApplied: true,
	isQuickFilter: false,
	isDefault: false,
	valuesAreIncluded: true,
	values: [
		{
			id,
			level: null,
			selection_depth: null,
			model: null,
			sort_order: null,
			label,
		},
	],
});
